/**
 * `orange-shell` — prototype `renderShell()`.
 *
 * Two chromes, one page area. The eShop chrome is masthead + breadcrumbs + footer;
 * the MyOrange chrome is a single 48px bar and nothing else. `ShellContext` decides,
 * from `?context=webview`, and the choice is sticky for the session — the query
 * string does not survive the first in-app navigation.
 *
 * ### Where the chrome's content comes from
 *
 * Route `data`, read as `PageMeta`. Crumbs, webview title, page measure and whether
 * the step bar shows are fixed per screen, so declaring them on the route means the
 * shell has them before the component is created. A screen that pushed them into a
 * service on init would render one frame carrying the previous screen's crumbs.
 *
 * The deepest route with a `meta` wins, so a child route can override its parent's.
 *
 * ### The step bar lives here
 *
 * As in the prototype, above the page content rather than inside each screen — one
 * placement to get right instead of eighteen. Which steps exist depends on the
 * variant, which is client state, so the shell reads it from `MigrationDraftStore`.
 *
 * ### And so does the guard's block
 *
 * `sessionGuard` cannot render, so it writes the block it resolved to
 * `AccessBlockStore` and the shell puts it where the outlet would have gone. Same
 * URL, same breadcrumbs, same measure — S-20 is a pattern rendered in place, not a
 * destination. The screen component is never created, which is the point: a blocked
 * payment screen must not fire the payment screen's queries.
 *
 * The step bar goes with it. A stepper over a dead end says the customer is at step
 * five of seven, which is the opposite of what the block is telling them.
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
} from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { AccessBlockStore } from '../core/access/access-block.store';
import { runBlockAction } from '../core/access/block-actions';
import { Annotations } from '../core/dev/annotations';
import { ROUTE_FOR, previousScreen, stepsFor } from '../core/flow/flow-steps';
import type { FlowScreenId } from '../core/flow/flow-steps';
import type { PageMeta } from '../core/flow/page-meta';
import { MigrationDraftStore } from '../core/stores/migration-draft.store';
import { SessionStore } from '../core/stores/session.store';
import { AnnoMarksComponent } from '../dev/anno-marks/anno-marks.component';
import { BlockedStateComponent } from '../ui/blocked-state/blocked-state.component';
import { LoadingComponent } from '../ui/loading/loading.component';
import { StepperComponent } from '../ui/stepper/stepper.component';
import { FooterComponent } from './footer/footer.component';
import { MastheadComponent } from './masthead/masthead.component';
import { ShellContext } from './shell-context';
import { SubnavComponent } from './subnav/subnav.component';
import { WebviewBarComponent } from './webview-bar/webview-bar.component';

const FALLBACK: PageMeta = {
  crumbs: [$localize`:@@crumbs.business:Business`],
  webviewTitle: $localize`:@@webview.defaultTitle:Orange Business`,
};

@Component({
  selector: 'orange-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    MastheadComponent,
    SubnavComponent,
    FooterComponent,
    WebviewBarComponent,
    StepperComponent,
    BlockedStateComponent,
    LoadingComponent,
    AnnoMarksComponent,
  ],
  template: `
    <div class="shell">
      @if (isWebview()) {
        <orange-webview-bar
          [title]="meta().webviewTitle"
          (backRequested)="goBack()"
          (closeRequested)="requestClose()"
        />
      } @else {
        <orange-masthead />
        <orange-subnav [crumbs]="meta().crumbs" />
      }

      <!-- The skip link in app.component.ts targets this. The tabindex is what
           makes the jump actually move focus rather than only scroll. -->
      <main class="page" id="main" tabindex="-1">
        <div [class]="meta().width === 'narrow' ? 'shell-narrow' : 'shell-width'">
          <!-- Placed above the stepper so it never displaces product layout mid-page,
               and deferred so the annotation registry — fifty-odd paragraphs of Romanian
               prototype metadata — stays out of the customer's bundle. The component
               renders nothing when the layer is off, so no inner @if is needed: once the
               chunk has loaded it is harmless. -->
          @defer (when annotationsOn()) {
            <orange-anno-marks />
          }

          @if (steps().length > 0) {
            <div class="steps">
              <orange-stepper [steps]="steps()" [compact]="isWebview()" />
            </div>
          }

          @if (accessBlock(); as block) {
            <orange-blocked-state
              [reason]="block.reason"
              [outcome]="block.outcome"
              [message]="block.message"
              [assistedSalesPhone]="block.assistedSalesPhone"
              [openQuestion]="block.openQuestion"
              [reference]="reference()"
              (actioned)="onBlockAction($event)"
            />
          } @else {
            @if (waiting()) {
              <orange-loading kind="spinner" [label]="waitingLabel" />
            }
            <router-outlet
              (activate)="activated.set(true)"
              (deactivate)="activated.set(false)"
            />
          }
        </div>
      </main>

      @if (!isWebview()) {
        <orange-footer />
      }
    </div>
  `,
  styles: [
    `
      .shell {
        background: var(--surface);
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      }

      /* No horizontal padding here — .shell-width and .shell-narrow carry the
         gutter themselves, and adding it twice would inset the page by 30px. */
      .page {
        flex: 1 1 auto;
        padding: var(--space-4) 0 var(--space-5);
      }

      /* The skip target is focusable but is not a control — it gets no ring. */
      .page:focus {
        outline: none;
      }

      .steps {
        margin-bottom: var(--space-4);
      }
    `,
  ],
})
export class ShellComponent {
  private readonly shell = inject(ShellContext);
  private readonly draft = inject(MigrationDraftStore);
  private readonly session = inject(SessionStore);
  private readonly access = inject(AccessBlockStore);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly anno = inject(Annotations);

  readonly isWebview = this.shell.isWebview;

  /**
   * Recomputed on every completed navigation. `ActivatedRoute` at the shell's own
   * level never changes — the shell is above the outlet — so the router's event
   * stream is the signal that something moved.
   *
   * Meta and URL travel together because they are read from the same instant: the
   * block a guard resolved is keyed to the URL it resolved it for, so a URL from one
   * navigation and a block from another would render the wrong page's dead end.
   */
  private readonly nav$ = this.router.events.pipe(
    filter((e) => e instanceof NavigationEnd),
    startWith(null),
    map(() => this.snapshot()),
  );

  private readonly nav = toSignal(this.nav$, { initialValue: this.snapshot() });

  readonly meta = computed(() => this.nav().meta);

  /** The block `sessionGuard` resolved for the page currently on screen, if any. */
  readonly accessBlock = computed(() => this.access.blockFor(this.nav().url));

  /**
   * The OCN, for the block's support reference line. Present on every block that
   * came from a check after the account was resolved; null for the anonymous and
   * no-OCN ones, where the component omits the line.
   */
  readonly reference = computed(() => this.session.business()?.ocn ?? null);

  /**
   * ### Why the page area has a loading state of its own
   *
   * `sessionGuard` awaits the whole eligibility chain — four ports, each with the
   * mocked call latency — before the router will activate anything. On a cold load
   * of a guarded URL that is the better part of two seconds during which the
   * masthead and breadcrumbs are up and the page area is empty, which reads as a
   * screen that failed rather than one that is coming.
   *
   * Shown only when the outlet holds nothing: mid-journey the router keeps the
   * outgoing screen alive until the incoming one activates, and replacing a screen
   * the customer is reading with a spinner is worse than the wait. `orange-loading`
   * adds its own delay on top, so a navigation that resolves quickly shows nothing
   * at all.
   */
  readonly activated = signal(false);

  private readonly navigating = toSignal(
    this.router.events.pipe(
      filter(
        (e) =>
          e instanceof NavigationStart ||
          e instanceof NavigationEnd ||
          e instanceof NavigationCancel ||
          e instanceof NavigationError ||
          e instanceof NavigationSkipped,
      ),
      map((e) => e instanceof NavigationStart),
    ),
    // True before the first event, because the router's initial navigation is
    // already in flight by the time this component is created.
    { initialValue: true },
  );

  readonly waiting = computed(() => this.navigating() && !this.activated());

  readonly waitingLabel = $localize`:@@shell.loading:Se încarcă…`;

  /**
   * Tell the annotation layer which screen is on.
   *
   * The marks and the overlay's *Adnotări* tab are in different trees, so the answer is
   * held on `Annotations` rather than passed down as an input. The shell is the only thing
   * that reads `PageMeta`, so it is the only thing that can report it; the four order
   * screens set an override from the status they resolved, which wins.
   *
   * `allowSignalWrites` is not needed — this writes to a service, and the layer is not
   * read anywhere in this component's own template.
   */
  private readonly reportScreen = effect(() => {
    this.anno.setRouteScreen(this.meta().screen ?? null);
  });

  /** The `@defer` trigger for the marks. */
  readonly annotationsOn = this.anno.active;

  readonly steps = computed(() => {
    const screen = this.meta().screen;
    if (!screen || this.meta().steps === false) return [];
    // No stepper over a block. See the note at the top of this file.
    if (this.accessBlock() !== null) return [];
    return stepsFor(this.draft.effectiveVariant(), screen, this.skippedSteps());
  });

  /**
   * S-13 is skipped, not completed, when a handset is in the order at full price.
   *
   * The declined-instalments treatment `OFFER_FULL_PRICE` routes S-12 → S-04, so from
   * S-04 onwards the draft holds a device and no instalment plan. That combination is
   * the fact — it is equally true of a deep link to a later screen — and it is only
   * meaningful once the customer is past the step, which `stepsFor` decides.
   */
  private readonly skippedSteps = computed<readonly FlowScreenId[]>(() =>
    this.draft.pendingDeviceId() !== null && this.draft.pendingInstalmentPlanId() === null
      ? ['S-13']
      : [],
  );

  /**
   * The webview bar's back control. Uses the flow's own order rather than browser
   * history: history back from a screen reached by a redirect lands somewhere the
   * customer never chose to be.
   */
  goBack(): void {
    const screen = this.meta().screen;
    const previous = screen ? previousScreen(this.draft.effectiveVariant(), screen) : null;
    if (previous) {
      void this.router.navigateByUrl(ROUTE_FOR[previous]);
    }
  }

  /**
   * In MyOrange the ✕ hands control back to the host app. What the host does with a
   * half-finished migration — whether the draft survives, whether the customer is
   * warned — is not specified anywhere (Q-38), and there is no host here to ask.
   * Deliberately does nothing rather than inventing a bridge protocol or a
   * confirmation dialog that would be read as the agreed integration contract.
   */
  requestClose(): void {
    /* Q-38. See above. */
  }

  /**
   * Five of the eight action ids go nowhere, and that is the answer — see
   * `runBlockAction`. The unhandled result is ignored here, because `runBlockAction`
   * has already recorded it: the overlay's Diagnostic tab lists every unhandled press
   * with a count on the tab itself, which is where an unimplemented action belongs.
   * A press that does nothing on screen is indistinguishable from a bug otherwise.
   */
  onBlockAction(action: string): void {
    runBlockAction(action, {
      router: this.router,
      session: this.session,
      url: this.router.url,
    });
  }

  private snapshot(): { readonly meta: PageMeta; readonly url: string } {
    return { meta: deepestMeta(this.route), url: this.router.url };
  }
}

/** The deepest route carrying `meta`, so a child can override its parent. */
function deepestMeta(root: ActivatedRoute): PageMeta {
  let route: ActivatedRoute | null = root;
  let found: PageMeta | null = null;

  while (route) {
    // Bracket access, not `.meta`: `Data` is an index signature and
    // `noPropertyAccessFromIndexSignature` is on, so the dot form is TS4111.
    // Biome's `useLiteralKeys` demands the opposite and is off in `biome.json`.
    const meta = route.snapshot.data['meta'] as PageMeta | undefined;
    if (meta) found = meta;
    route = route.firstChild;
  }

  return found ?? FALLBACK;
}
